/**
 * Comments API — field / form / record scoped, with resolution + threading.
 */
import { supabase } from '@/lib/supabase/client'
import { toDbRole, type ProjectRole } from '@/features/auth/context/project-role-context'
import { CommentResolutionForbiddenError } from './errors'
import type { CommentRow } from './types'

/**
 * The author columns every comment insert shares. A Super User has no project
 * role, so the role is stored NULL alongside the flag — see toDbRole.
 */
function authorColumns(userId: string, role: ProjectRole) {
  const { dbRole, isSuperUser } = toDbRole(role)
  return {
    author_id: userId,
    author_role_at_time: dbRole,
    is_super_user_comment: isSuperUser,
  }
}

// ─── Fetches ──────────────────────────────────────────────────────────────────

export async function fetchDeclineComments(recordId: string) {
  const { data, error } = await supabase
    .from('comments')
    .select('*, author:user_profiles!author_id(full_name)')
    .eq('record_id', recordId)
    .eq('origin', 'decline')
    .eq('is_resolved', false)
    .eq('is_deleted', false)
    .order('created_at', { ascending: true })
  if (error) throw error
  return (data ?? []) as CommentRow[]
}

export async function fetchFieldComments(recordId: string, submissionId: string) {
  const { data, error } = await supabase
    .from('comments')
    .select('*, author:user_profiles!author_id(full_name)')
    .eq('record_id', recordId)
    .eq('submission_id', submissionId)
    .eq('scope', 'field')
    .eq('is_deleted', false)
    .order('created_at', { ascending: true })
  if (error) throw error
  return (data ?? []) as CommentRow[]
}

export async function fetchFormComments(recordId: string, submissionId: string) {
  const { data, error } = await supabase
    .from('comments')
    .select('*, author:user_profiles!author_id(full_name)')
    .eq('record_id', recordId)
    .eq('submission_id', submissionId)
    .eq('scope', 'form')
    .eq('is_deleted', false)
    .order('created_at', { ascending: true })
  if (error) throw error
  return (data ?? []) as CommentRow[]
}

export async function fetchRecordComments(recordId: string) {
  const { data, error } = await supabase
    .from('comments')
    .select('*, author:user_profiles!author_id(full_name)')
    .eq('record_id', recordId)
    .eq('scope', 'record')
    .eq('is_deleted', false)
    .order('created_at', { ascending: true })
  if (error) throw error
  return (data ?? []) as CommentRow[]
}

// ─── Writes ───────────────────────────────────────────────────────────────────

export async function addFieldComment(params: {
  recordId: string
  submissionId: string
  fieldKey: string
  body: string
  userId: string
  userRole: ProjectRole
}) {
  const { error } = await supabase.from('comments').insert({
    record_id: params.recordId,
    submission_id: params.submissionId,
    field_key: params.fieldKey,
    scope: 'field',
    body: params.body,
    ...authorColumns(params.userId, params.userRole),
    origin: 'general',
  })
  if (error) throw error
}

export async function addFormComment(params: {
  recordId: string
  submissionId: string
  body: string
  userId: string
  userRole: ProjectRole
}) {
  const { error } = await supabase.from('comments').insert({
    record_id: params.recordId,
    submission_id: params.submissionId,
    scope: 'form',
    body: params.body.trim(),
    ...authorColumns(params.userId, params.userRole),
    origin: 'general',
  })
  if (error) throw error
}

export async function addRecordComment(params: {
  recordId: string
  body: string
  userId: string
  userRole: ProjectRole
}) {
  const { error } = await supabase.from('comments').insert({
    record_id: params.recordId,
    scope: 'record',
    body: params.body.trim(),
    ...authorColumns(params.userId, params.userRole),
    origin: 'general',
  })
  if (error) throw error
}

export async function addReplyComment(params: {
  recordId: string
  parentCommentId: string
  body: string
  userId: string
  userRole: ProjectRole
  submissionId?: string
  fieldKey?: string
  scope: 'field' | 'form' | 'record'
}) {
  const { error } = await supabase.from('comments').insert({
    record_id: params.recordId,
    submission_id: params.submissionId ?? null,
    field_key: params.fieldKey ?? null,
    scope: params.scope,
    body: params.body.trim(),
    ...authorColumns(params.userId, params.userRole),
    origin: 'general',
    parent_comment_id: params.parentCommentId,
  })
  if (error) throw error
}

export async function updateComment(commentId: string, body: string) {
  const { error } = await supabase
    .from('comments')
    .update({ body: body.trim(), is_edited: true, updated_at: new Date().toISOString() })
    .eq('id', commentId)
    .eq('is_deleted', false)
  if (error) throw error
}

export async function deleteComment(commentId: string, userId: string) {
  const { error } = await supabase
    .from('comments')
    .update({ is_deleted: true, deleted_at: new Date().toISOString(), deleted_by: userId })
    .eq('id', commentId)
  if (error) throw error
}

/**
 * Resolve a comment via the role-gated `resolve_comment` RPC (cluster D, D3).
 * Permission matrix:
 *   - decline-origin: resolvable by data_entry / admin / approver / super,
 *     EXCEPT the author of the decline comment (no self-resolve).
 *   - general-origin: resolvable by the author OR by admin / approver / super.
 */
export async function resolveComment(commentId: string, userId: string) {
  const { data, error } = await supabase.rpc('resolve_comment', {
    p_comment_id: commentId,
    p_user_id: userId,
  })
  if (error) throw error
  const result = data as { success?: boolean; error?: string }
  if (result?.error === 'forbidden') throw new CommentResolutionForbiddenError('forbidden')
  if (result?.error === 'author_cannot_self_resolve')
    throw new CommentResolutionForbiddenError('author_cannot_self_resolve')
  if (result?.error === 'deleted') throw new CommentResolutionForbiddenError('deleted')
  if (result?.error === 'not_found') throw new Error('نظر یافت نشد')
  if (result?.error) throw new Error(result.error)
}
